"use client";

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../../constants/config";
import { extractApiErrorMessage } from "../../../utils/error-handler";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const BillingClients = () => {
  const navigate = useNavigate();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    axiosInstance
      .get(API_ENDPOINTS.schoolsStats)
      .then((res) => setSchools(res.data?.data?.schools || []))
      .catch((err) => setError(extractApiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, []);

  const industryClients = useMemo(() => {
    const query = search.trim().toLowerCase();
    return schools
      .filter((s) => (s?.tenantType || "school").toLowerCase() === "industry")
      .filter((s) => {
        if (!query) return true;
        const haystack = `${s.name || ""} ${s.domain || ""}`.toLowerCase();
        return haystack.includes(query);
      });
  }, [schools, search]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Billing Clients</h1>
          <p className="text-sm text-gray-500 mt-0.5">Industry Inventory tenants — click one to manage their billing.</p>
        </div>
        <Button variant="outline" onClick={() => navigate("/dashboard/billing")}>
          Back to Billing
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or domain..." className="pl-9" />
      </div>

      {error && <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700 text-sm">{error}</div>}

      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-x-auto">
        {loading ? (
          <div className="py-16 text-center text-gray-400 text-sm">Loading...</div>
        ) : industryClients.length === 0 ? (
          <div className="py-16 text-center text-gray-400 text-sm">No Industry clients found.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Domain</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {industryClients.map((school) => (
                <TableRow
                  key={school._id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => navigate(`/dashboard/schools/${school._id}/billing`)}
                >
                  <TableCell className="font-medium">{school.name}</TableCell>
                  <TableCell>{school.domain}</TableCell>
                  <TableCell>
                    <Badge variant={school.isActive !== false ? "default" : "secondary"}>
                      {school.isActive !== false ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/dashboard/schools/${school._id}/billing`);
                      }}
                    >
                      View Billing
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};

export default BillingClients;
